import React, { useCallback, useMemo } from "react";
import { useLocation, navigate } from "@reach/router";
import { SettingsPageLayout } from "newui/features/Settings/components/SettingsPageLayout";
import type { TabMenuItem } from "newui/components/TabComponent/TabComponent";
import type { Company } from "newui/features/Company/types";
import { ScoringInstructions } from "./components/ScoringInstructions/ScoringInstructions";
import { EvaluationPermissions } from "./components/EvaluationPermissions/EvaluationPermissions";
import { PanelDisclaimerSettings } from "./components/PanelDisclaimerSettings";
import { useProfileContext } from "newui/features/Auth/context/ProfileContext";
import { QuoteTemplateSettings } from "newui/features/Rfx/components/QuoteTable/QuoteTableSettings";
import { CompanyScheduleSettings } from "../Rfx/components/ScheduleOfRates";
import { SubmissionComplianceTab } from "./SubmissionComplianceTab";
import { useCurrentUserHasFeatureFlag } from "hooks/featureFlags";
import { useModuleCan } from "newui/features/RolesPermissions/hooks/useModuleCan";
import { RBAC_MODULE_KEYS } from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { NoAccessMessage, ReadOnlySurface } from "newui/components/AccessGate";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";
import { FeatureFlagType } from "../Auth/types/FeatureFlag.type";

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

interface RfxSettingsProps {
  company: Company;
  /** Path prop is consumed by @reach/router; not used directly. */
  path?: string;
}

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

const SCORING_INSTRUCTIONS = "Scoring Instructions";
const EVALUATION_PERMISSIONS = "Evaluation Permissions";
const SCHEDULE_OF_RATES = "Schedule of Rates";
const QUOTE_TEMPLATES = "Quote Templates";
const PANEL_MEMBER_DISCLAIMER = "Panel Member Disclaimer";
const SUBMISSION_COMPLIANCE = "Submission Compliance";

const EVAL_PROCURER_TABS = [
  { id: "submission-compliance", name: SUBMISSION_COMPLIANCE, showBadge: false, total: 0 },
  { id: "evaluation-permissions", name: EVALUATION_PERMISSIONS, showBadge: false, total: 0 },
  { id: "scoring-instructions", name: SCORING_INSTRUCTIONS, showBadge: false, total: 0 },
  { id: "panel-member-disclaimer", name: PANEL_MEMBER_DISCLAIMER, showBadge: false, total: 0 },
]

const ALL_PROCURER_TABS: TabMenuItem[] = [
  { id: "quote-templates", name: QUOTE_TEMPLATES, showBadge: false, total: 0 },
];

const SUPPLIER_TABS: TabMenuItem[] = [
  { id: "schedule-of-rates", name: SCHEDULE_OF_RATES, showBadge: false, total: 0 },
];

const SLUG_TO_NAME: Record<string, string> = {
  "scoring-instructions": SCORING_INSTRUCTIONS,
  "evaluation-permissions": EVALUATION_PERMISSIONS,
  "schedule-of-rates": SCHEDULE_OF_RATES,
  "quote-templates": QUOTE_TEMPLATES,
  "panel-member-disclaimer": PANEL_MEMBER_DISCLAIMER,
  "submission-compliance": SUBMISSION_COMPLIANCE,
};

const NAME_TO_SLUG: Record<string, string> = Object.entries(SLUG_TO_NAME).reduce(
  (acc, [slug, name]) => ({ ...acc, [name]: slug }),
  {}
);

/*
 * PRO-709 — every tab on this page used to hang off the single `settings`
 * module, so "can edit the company's quote templates" and "can edit the
 * evaluation scoring instructions" were the same grant. The backend split
 * `settings` per surface; this map is the FE half of that split.
 *
 * The four evaluation tabs deliberately share ONE key — they are one
 * administrative concern, not four.
 */
const TAB_MODULE_KEY: Record<string, string> = {
  [QUOTE_TEMPLATES]: RBAC_MODULE_KEYS.rfxSettingsQuoteTemplates,
  [SUBMISSION_COMPLIANCE]: RBAC_MODULE_KEYS.rfxSettingsEvaluation,
  [EVALUATION_PERMISSIONS]: RBAC_MODULE_KEYS.rfxSettingsEvaluation,
  [SCORING_INSTRUCTIONS]: RBAC_MODULE_KEYS.rfxSettingsEvaluation,
  [PANEL_MEMBER_DISCLAIMER]: RBAC_MODULE_KEYS.rfxSettingsEvaluation,
  [SCHEDULE_OF_RATES]: RBAC_MODULE_KEYS.rfxSettingsScheduleOfRates,
};

interface TabAccess {
  canEdit: boolean;
  canOpen: boolean;
}

/* -------------------------------------------------------------------------- */
/* URL parsing                                                                */
/* -------------------------------------------------------------------------- */

interface ParsedRoute {
  basePath: string; // everything up to and including /rfx-settings
  editingId: number | null;
  isNew: boolean;
  tabSlug: string | null;
}

const parseRoute = (pathname: string): ParsedRoute => {
  const marker = "/rfx-settings";
  const idx = pathname.indexOf(marker);
  if (idx === -1) {
    return { basePath: pathname.replace(/\/$/, ""), tabSlug: null, editingId: null, isNew: false };
  }
  const basePath = pathname.slice(0, idx + marker.length);
  const remainder = pathname.slice(idx + marker.length).replace(/^\/+|\/+$/g, "");
  if (!remainder) {
    return { basePath, tabSlug: null, editingId: null, isNew: false };
  }
  const [slug, second] = remainder.split("/");
  if (!second) {
    return { basePath, tabSlug: slug, editingId: null, isNew: false };
  }
  if (second === "new") {
    return { basePath, tabSlug: slug, editingId: null, isNew: true };
  }
  const parsed = Number(second);
  return {
    basePath,
    tabSlug: slug,
    editingId: Number.isFinite(parsed) ? parsed : null,
    isNew: false,
  };
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export const RfxSettings = ({ company }: RfxSettingsProps) => {
  const { isClientProfileActive } = useProfileContext();
  const location = useLocation();
  const hasEvaluation = useCurrentUserHasFeatureFlag(FeatureFlagType.ENABLE_RFX_EVALUATION);

  const quoteTemplates = useModuleCan(RBAC_MODULE_KEYS.rfxSettingsQuoteTemplates);
  const evaluation = useModuleCan(RBAC_MODULE_KEYS.rfxSettingsEvaluation);
  const scheduleOfRates = useModuleCan(RBAC_MODULE_KEYS.rfxSettingsScheduleOfRates);

  const isPermissionsLoading =
    quoteTemplates.isLoading || evaluation.isLoading || scheduleOfRates.isLoading;

  /*
   * `canOpen` is view OR edit: the server sends both flags, and an edit grant
   * without view is not a state the matrix produces — but treating edit as
   * implying access means a capability payload that omits `view` still opens
   * the tab rather than hiding a surface the user can write to.
   */
  const accessByModule: Record<string, TabAccess> = useMemo(
    () => ({
      [RBAC_MODULE_KEYS.rfxSettingsQuoteTemplates]: {
        canOpen: quoteTemplates.canView || quoteTemplates.canEdit,
        canEdit: quoteTemplates.canEdit,
      },
      [RBAC_MODULE_KEYS.rfxSettingsEvaluation]: {
        canOpen: evaluation.canView || evaluation.canEdit,
        canEdit: evaluation.canEdit,
      },
      [RBAC_MODULE_KEYS.rfxSettingsScheduleOfRates]: {
        canOpen: scheduleOfRates.canView || scheduleOfRates.canEdit,
        canEdit: scheduleOfRates.canEdit,
      },
    }),
    [
      quoteTemplates.canView,
      quoteTemplates.canEdit,
      evaluation.canView,
      evaluation.canEdit,
      scheduleOfRates.canView,
      scheduleOfRates.canEdit,
    ]
  );

  const profileTabs = useMemo(
    () => (isClientProfileActive ?
      hasEvaluation ? ALL_PROCURER_TABS.concat(EVAL_PROCURER_TABS) : ALL_PROCURER_TABS : SUPPLIER_TABS),
    [isClientProfileActive, hasEvaluation]
  );

  /*
   * A tab the user holds no capability on is not rendered AND is not routable:
   * `resolvedTabName` only honours a slug that survives this filter, so typing
   * /rfx-settings/quote-templates lands on the first permitted tab instead.
   */
  const tabs = useMemo(
    () =>
      profileTabs.filter(
        (tab) => accessByModule[TAB_MODULE_KEY[tab.name]]?.canOpen === true
      ),
    [profileTabs, accessByModule]
  );

  const defaultTab = tabs[0]?.name ?? SCORING_INSTRUCTIONS;
  const defaultSlug = tabs[0]?.id ?? "scoring-instructions";

  const parsed = useMemo(() => parseRoute(location.pathname), [location.pathname]);

  // Resolve the active tab from the URL, falling back to the first available tab.
  const resolvedTabName = useMemo(() => {
    if (parsed.tabSlug && SLUG_TO_NAME[parsed.tabSlug]) {
      const name = SLUG_TO_NAME[parsed.tabSlug];
      // Only honour the slug if it belongs to the current profile's tabs.
      if (tabs.some((t) => t.name === name)) {
        return name;
      }
    }
    return defaultTab;
  }, [parsed.tabSlug, tabs, defaultTab]);

  const changeActiveTab = useCallback(
    (name: string): void => {
      const slug = NAME_TO_SLUG[name] ?? defaultSlug;
      navigate(`${parsed.basePath}/${slug}`);
    },
    [parsed.basePath, defaultSlug]
  );

  const handleQuoteTemplateEditingChange = useCallback(
    (id: number | null, opts?: { isNew?: boolean }) => {
      const base = `${parsed.basePath}/quote-templates`;
      if (opts?.isNew) {
        navigate(`${base}/new`);
        return;
      }
      navigate(id == null ? base : `${base}/${id}`);
    },
    [parsed.basePath]
  );

  const isActiveTabReadOnly =
    accessByModule[TAB_MODULE_KEY[resolvedTabName]]?.canEdit !== true;

  if (isPermissionsLoading) {
    return <LoadingSpinner label="Loading RFx Settings..." verticalPadding={8} />;
  }

  if (tabs.length === 0) {
    return (
      <NoAccessMessage
        data-testid="rfx-settings-no-access"
        description="You do not have permission to view RFx Settings. Ask a company administrator if you need access."
      />
    );
  }

  const activeTabContent = (
    <>
      {resolvedTabName === SCORING_INSTRUCTIONS && (
        <ScoringInstructions company={company} />
      )}
      {resolvedTabName === EVALUATION_PERMISSIONS && (
        <EvaluationPermissions company={company} />
      )}
      {resolvedTabName === SCHEDULE_OF_RATES && company?.id && (
        <CompanyScheduleSettings companyId={company.id} />
      )}
      {resolvedTabName === QUOTE_TEMPLATES && company?.id && (
        <QuoteTemplateSettings
          companyId={company.id}
          editingId={parsed.editingId}
          isCreatingNew={parsed.isNew}
          onEditingChange={handleQuoteTemplateEditingChange}
        />
      )}
      {resolvedTabName === PANEL_MEMBER_DISCLAIMER && (
        <PanelDisclaimerSettings company={company} />
      )}
      {resolvedTabName === SUBMISSION_COMPLIANCE && (
        <SubmissionComplianceTab company={company} />
      )}
    </>
  );

  return (
    <SettingsPageLayout
      heading="RFx Settings"
      tabs={tabs}
      activeTab={resolvedTabName}
      onTabChange={changeActiveTab}
    >
      {isActiveTabReadOnly ? (
        <ReadOnlySurface
          data-testid="rfx-settings-read-only"
          message="You have read-only access to this tab. Saving, creating and deleting are disabled."
        >
          {activeTabContent}
        </ReadOnlySurface>
      ) : (
        activeTabContent
      )}
    </SettingsPageLayout>
  );
};
